// GuideLayout — shell for every User Guide section page.
//
// The portal already owns the left rail, so the guide's in-page navigation lives
// on the RIGHT as a sticky table of contents. The TOC is derived from the rendered
// DOM (`h2[id], h3[id]`) rather than a hand-kept list, so it can never drift from
// the content. Below xl the TOC is hidden and the prose gets the full width.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';

export default function GuideLayout({ title, subtitle, icon: Icon, children }) {
  const contentRef = useRef(null);
  const [toc, setToc] = useState([]);
  const [activeId, setActiveId] = useState('');

  // Build the TOC from whatever the page actually rendered.
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;
    const nodes = Array.from(root.querySelectorAll('h2[id], h3[id]'));
    setToc(nodes.map((n) => ({ id: n.id, text: n.textContent || '', level: n.tagName === 'H2' ? 2 : 3 })));
  }, [children]);

  // Scroll-spy: the last heading whose top has passed the reading line wins.
  // The portal scrolls <main>, not the window, so listen on the nearest scroll
  // container and fall back to window for safety.
  const onScroll = useCallback(() => {
    const root = contentRef.current;
    if (!root) return;
    const nodes = Array.from(root.querySelectorAll('h2[id], h3[id]'));
    let current = '';
    for (const n of nodes) {
      if (n.getBoundingClientRect().top <= 120) current = n.id;
    }
    if (current) setActiveId(current);
    else if (nodes.length) setActiveId(nodes[0].id);
  }, []);

  useEffect(() => {
    const scroller = contentRef.current?.closest('main') || window;
    scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      window.removeEventListener('scroll', onScroll);
    };
  }, [onScroll, toc.length]);

  const jump = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveId(id);
    }
  };

  return (
    // p-6 matches the gutter every other portal page sets for itself — <main>
    // supplies none, so without it the prose butts against the sidebar.
    <div className="flex gap-8 items-start max-w-[1400px] p-6">
      {/* ── Prose column ── */}
      <div ref={contentRef} className="flex-1 min-w-0 max-w-3xl">
        <Link
          to="/guide"
          className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors mb-4"
        >
          <ChevronLeftIcon className="h-3.5 w-3.5" />
          User Guide
        </Link>

        <div className="flex items-start gap-3 mb-2">
          {Icon && <Icon className="h-6 w-6 text-blue-400 flex-none mt-0.5" />}
          <div>
            <h1 className="text-xl font-bold text-gray-100 leading-tight">{title}</h1>
            {subtitle && <p className="text-sm text-gray-500 mt-1 leading-relaxed">{subtitle}</p>}
          </div>
        </div>

        {children}

        <div className="mt-16 pt-4 border-t border-gray-800 text-xs text-gray-600">
          Blue365Signal User Guide · portal v{__APP_VERSION__}
        </div>
      </div>

      {/* ── Sticky table of contents ── */}
      <nav className="hidden xl:block w-60 flex-none sticky top-6 max-h-[calc(100vh-3rem)] overflow-y-auto">
        <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mb-2 px-2">
          On this page
        </p>
        <ul className="space-y-0.5 border-l border-gray-800">
          {toc.map((h) => (
            <li key={h.id}>
              <a
                href={`#${h.id}`}
                onClick={jump(h.id)}
                className={`block py-1 text-xs leading-snug transition-colors border-l -ml-px ${
                  h.level === 3 ? 'pl-5' : 'pl-3'
                } ${
                  activeId === h.id
                    ? 'border-blue-500 text-blue-400 font-medium'
                    : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600'
                }`}
              >
                {h.text}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
